/**
 * Fixed priority order for the three always-mounted toast managers, used by
 * canClaimToastSlot to decide who may preempt whom. Higher number wins.
 * Customer updates are the most time-sensitive for a service advisor;
 * automation toasts are ambient, shop-wide broadcasts and always yield.
 *
 * AUTOMATION is deliberately 0 — equal to, not above, the default applied to
 * an untagged occupant (`ownerPriority ?? 0`). Because canClaimToastSlot
 * requires a *strictly* lower occupant, that equality is what makes
 * automation defer to untagged toasts as well as to the two named managers,
 * exactly reproducing the behavior of the isToastSlotFreeOrOurs helper it
 * replaced ("defer to anyone but me"). Untagged toasts are real and
 * user-initiated — the success/error toasts raised from Shop.form.tsx,
 * UserDetailsDialog.tsx and useTaskListActions.ts — so an ambient broadcast
 * preempting one would be a genuine regression, not a tidier ordering.
 */
export const TOAST_PRIORITY = {
  CUSTOMER_UPDATE: 3,
  OVERDUE_TASK: 2,
  AUTOMATION: 0,
} as const;

/**
 * Whether a manager at `myPriority` may show/reclaim the shared toast slot
 * right now, given who (if anyone) currently occupies it.
 *
 * Generalizes automationToastPlan.ts's isToastSlotFreeOrOurs (which
 * hardcoded "defer to anyone but me" -- equivalent to always being the
 * lowest priority) into a priority-aware version shared by all three
 * managers. An occupant with no ownerPriority tag is treated as priority 0,
 * so an untagged/legacy toast can never permanently block a *higher*-priority
 * manager (overdue task, customer update) from claiming the slot -- while
 * AUTOMATION, pinned to that same 0, still yields to it. The comparison is
 * strict (`<`) rather than `<=`, which is what makes ties defer.
 * @param {object} params - Inputs describing occupancy and the caller's priority.
 * @param {ReadonlyArray<{id: string; ownerPriority?: number}>} params.toasts - Current shared toast store contents.
 * @param {number} params.myPriority - The calling manager's fixed priority (see TOAST_PRIORITY).
 * @param {string | null} params.myToastId - The id of the toast this manager currently owns, if any.
 * @returns {boolean} True if the caller may show/reclaim the slot now.
 */
export function canClaimToastSlot(params: {
  toasts: ReadonlyArray<{ id: string; ownerPriority?: number }>;
  myPriority: number;
  myToastId: string | null;
}): boolean {
  const { toasts, myPriority, myToastId } = params;
  const occupant = toasts[0];

  if (!occupant) return true;
  if (occupant.id === myToastId) return true;

  return (occupant.ownerPriority ?? 0) < myPriority;
}
